import type { FC } from 'react';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { BrowserRouter, Routes, Route } from 'react-router';

import Layout from '@/components/Layout';
import ProtectedRoute from '@/components/ProtectedRoute';
import WalletProtectedRoute from '@/components/WalletProtectedRoute';
import { ROUTES } from '@/constants';
import CampaignDetails from '@/pages/CampaignDetails';
import Campaigns from '@/pages/Campaigns';
import Dashboard from '@/pages/Dashboard';
import LaunchCampaignPage from '@/pages/LaunchCampaign';
import ManageApiKeysPage from '@/pages/ManageApiKeys';
import PreferencesPage from '@/pages/Preferences';
import ActiveAccountProvider from '@/providers/ActiveAccountProvider';
import { AuthedUserDataProvider } from '@/providers/AuthedUserData';
import ExchangesProvider from '@/providers/ExchangesProvider';
import { NetworkProvider } from '@/providers/NetworkProvider';
import { NotificationProvider } from '@/providers/NotificationProvider';
import QueryClientProvider from '@/providers/QueryClientProvider';
import SignerProvider from '@/providers/SignerProvider';
import ThemeProvider from '@/providers/ThemeProvider';
import WagmiProvider from '@/providers/WagmiProvider';
import { Web3AuthProvider } from '@/providers/Web3AuthProvider';

const App: FC = () => {
  return (
    <WagmiProvider>
      <QueryClientProvider>
        <ThemeProvider>
          <BrowserRouter>
            <NotificationProvider>
              <NetworkProvider>
                <ActiveAccountProvider>
                  <SignerProvider>
                    <Web3AuthProvider>
                      <AuthedUserDataProvider>
                        <ExchangesProvider>
                          <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale="en"
                          >
                            <Layout>
                              <Routes>
                                <Route
                                  path={ROUTES.DASHBOARD}
                                  element={<Dashboard />}
                                />
                                <Route
                                  path={ROUTES.CAMPAIGNS}
                                  element={<Campaigns />}
                                />
                                <Route
                                  path={ROUTES.CAMPAIGN_DETAILS}
                                  element={<CampaignDetails />}
                                />
                                <Route
                                  path={ROUTES.MANAGE_API_KEYS}
                                  element={
                                    <ProtectedRoute>
                                      <ManageApiKeysPage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path={ROUTES.LAUNCH_CAMPAIGN}
                                  element={
                                    <WalletProtectedRoute>
                                      <LaunchCampaignPage />
                                    </WalletProtectedRoute>
                                  }
                                />
                                <Route
                                  path={ROUTES.PREFERENCES}
                                  element={
                                    <ProtectedRoute>
                                      <PreferencesPage />
                                    </ProtectedRoute>
                                  }
                                />
                              </Routes>
                            </Layout>
                          </LocalizationProvider>
                        </ExchangesProvider>
                      </AuthedUserDataProvider>
                    </Web3AuthProvider>
                  </SignerProvider>
                </ActiveAccountProvider>
              </NetworkProvider>
            </NotificationProvider>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

export default App;
